import Close from "@mui/icons-material/Close";
import ErrorRounded from "@mui/icons-material/ErrorRounded";
import {
    Box,
    CircularProgress,
    Dialog,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import { SubscriptionCadence } from "components/Conversations/Views/Drawer/components/Subscriptions/helpers";
import AcxButton from "components/UI/AcxButton";
import AcxDateRangeInput from "components/UI/Calendar/DateRange/AcxDateRangeInput";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { AcxMessageBox } from "components/UI/AcxMessageBox";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import moment, { Moment } from "moment";
import React from "react";
import {
    KboFlowQuestionSubscription,
    RunKboFlowQuestionOnceRequest,
    SaveKboFlowQuestionSubscriptionRequest,
} from "services/KboFlowSubscriptionsService";
import { SparklingGradientMultiStar } from "SvgIcons/SparklingGradientStar";
import { KBO_BANNER_GRADIENT } from "./kboSubscriptionStyles";

const NAME_MAX_LENGTH = 100;
// OrchestrationEngine's question validation refuses longer questions.
const QUESTION_MAX_LENGTH = 1000;

/** Ultra-only frequency that starts one run now instead of creating a subscription. */
const ONCE_IMMEDIATELY = "onceImmediately";

type Frequency = SubscriptionCadence | typeof ONCE_IMMEDIATELY;
type FrequencyOption = { value: Frequency; label: string };

const onceImmediatelyOption: FrequencyOption = {
    value: ONCE_IMMEDIATELY,
    label: "Once Immediately",
};

const subscriptionFrequencyOptions: FrequencyOption[] = [
    { value: SubscriptionCadence.Weekly, label: "Weekly" },
    { value: SubscriptionCadence.Monthly, label: "Monthly" },
];

const frequencyNote = (frequency: Frequency) => {
    switch (frequency) {
        case ONCE_IMMEDIATELY:
            return "KBo starts researching this question now for the selected dates. This doesn't create a subscription or send an email.";
        case SubscriptionCadence.Monthly:
            return "Your emails will arrive on the first business day of every month.";
        default:
            return "Your emails will arrive on the first business day of every week.";
    }
};

const rejectionTextSx = {
    fontSize: 13,
    lineHeight: "18px",
    color: "#4B5563",
};

const fieldLabelSx = { fontSize: 13, lineHeight: "18px", color: "#374151" };
const helperSx = {
    fontSize: 12,
    lineHeight: "16px",
    color: "#6B7280",
    marginTop: "2px",
};

export type QuestionRejection = {
    questionText: string;
    reason?: string;
};

type Props = {
    isOpen: boolean;
    /** The subscription being edited; omitted when adding. */
    subscription?: KboFlowQuestionSubscription;
    saving: boolean;
    deleting: boolean;
    saveError?: string;
    /** KBo's verdict that it can't research a question, until that question is reworded. */
    rejection?: QuestionRejection;
    /** Offers "Once Immediately" when adding. Only ultra users may run a question once. */
    canRunOnce: boolean;
    onClose: () => void;
    onSave: (request: SaveKboFlowQuestionSubscriptionRequest) => void;
    onRunOnce: (request: RunKboFlowQuestionOnceRequest) => void;
    onDelete: () => void;
};

/**
 * Add / edit dialog for a question subscription. Delete is confirmed inline, replacing the
 * footer, rather than with a second dialog.
 */
export function QuestionSubscriptionDialog({
    isOpen,
    subscription,
    saving,
    deleting,
    saveError,
    rejection,
    canRunOnce,
    onClose,
    onSave,
    onRunOnce,
    onDelete,
}: Props) {
    const isEdit = Boolean(subscription);
    const [name, setName] = React.useState("");
    const [questionText, setQuestionText] = React.useState("");
    const [frequency, setFrequency] = React.useState<Frequency>(
        SubscriptionCadence.Weekly,
    );
    const [startDate, setStartDate] = React.useState<Moment>(() =>
        moment().subtract(30, "days"),
    );
    const [endDate, setEndDate] = React.useState<Moment>(() => moment());
    const [confirmingDelete, setConfirmingDelete] = React.useState(false);

    React.useEffect(() => {
        if (!isOpen) return;
        setName(subscription?.name ?? "");
        setQuestionText(subscription?.questionText ?? "");
        setFrequency(subscription?.cadence ?? SubscriptionCadence.Weekly);
        setStartDate(moment().subtract(30, "days"));
        setEndDate(moment());
        setConfirmingDelete(false);
    }, [isOpen, subscription]);

    const frequencyOptions =
        canRunOnce && !isEdit
            ? [onceImmediatelyOption, ...subscriptionFrequencyOptions]
            : subscriptionFrequencyOptions;
    const runningOnce = frequency === ONCE_IMMEDIATELY;
    const datesValid =
        startDate.isValid() &&
        endDate.isValid() &&
        !endDate.isBefore(startDate);

    // New questions, and edits that reword the question, are validated by KBo on save.
    const needsValidation =
        !runningOnce &&
        (!subscription ||
            questionText.trim() !== subscription.questionText.trim());
    const validating = saving && needsValidation;
    const rejected =
        !saving &&
        rejection !== undefined &&
        rejection.questionText === questionText.trim();

    const busy = saving || deleting;
    const canSave =
        !busy &&
        Boolean(questionText.trim()) &&
        (runningOnce ? datesValid : Boolean(name.trim()));

    const submit = () => {
        if (frequency === ONCE_IMMEDIATELY) {
            onRunOnce({
                questionText: questionText.trim(),
                startDateUtc: startDate.clone().startOf("day").toISOString(),
                endDateUtc: endDate.clone().endOf("day").toISOString(),
            });
        } else {
            onSave({
                name: name.trim(),
                questionText: questionText.trim(),
                cadence: frequency,
            });
        }
    };
    const close = () => {
        if (!busy) onClose();
    };

    return (
        <Dialog
            open={isOpen}
            onClose={close}
            fullWidth
            disableRestoreFocus
            PaperProps={{ sx: { maxWidth: 560, borderRadius: "8px" } }}
        >
            <Stack
                rowGap={0.75}
                sx={{
                    background: KBO_BANNER_GRADIENT,
                    padding: "20px 24px",
                    color: "#FFFFFF",
                }}
            >
                <Stack direction="row" alignItems="center" columnGap={1.25}>
                    <SparklingGradientMultiStar />
                    <Typography
                        color="inherit"
                        sx={{
                            flex: 1,
                            fontSize: 20,
                            lineHeight: "28px",
                            fontWeight: 600,
                        }}
                    >
                        {isEdit
                            ? "Edit KBo Flow Subscription"
                            : "Add KBo Flow Subscription"}
                    </Typography>
                    <IconButton
                        id="kbo-question-subscription-dialog-close"
                        aria-label="Close"
                        onClick={close}
                        disabled={busy}
                        sx={{
                            color: "inherit",
                            padding: "4px",
                            "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.12)",
                            },
                        }}
                    >
                        <Close />
                    </IconButton>
                </Stack>
                <Typography
                    sx={{ fontSize: 14, lineHeight: "21px" }}
                    color="inherit"
                >
                    {isEdit
                        ? "Update the question or how often KBo sends it. Changes apply to your next email."
                        : runningOnce
                        ? "Ask a question and KBo will research it across your conversations right away."
                        : "Ask a question and KBo will research it across your conversations, then deliver the analysis to your inbox."}
                </Typography>
            </Stack>

            <Stack rowGap={2.5} sx={{ padding: "20px 24px 8px" }}>
                {!runningOnce && (
                    <Stack rowGap={0.75}>
                        <Typography sx={fieldLabelSx}>Name</Typography>
                        <AcxMainTextField
                            id="kbo-question-subscription-name"
                            placeholderText="Example: Prior auth denials and patient access"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            maxLength={NAME_MAX_LENGTH}
                            fullWidth
                        />
                        <Typography sx={helperSx}>
                            Shown on your subscription card and in the email
                            subject line.
                        </Typography>
                    </Stack>
                )}

                <Stack rowGap={0.75}>
                    <Typography sx={fieldLabelSx}>Question</Typography>
                    <AcxMainTextField
                        id="kbo-question-subscription-question"
                        placeholderText="Example: What are the main reasons prior authorization denials are increasing, and how do they impact patient access?"
                        value={questionText}
                        onChange={(event) =>
                            setQuestionText(event.target.value)
                        }
                        maxLength={QUESTION_MAX_LENGTH}
                        multiline
                        rowsMin={2}
                        rowsMax={8}
                        fullWidth
                    />
                    <Typography sx={helperSx}>
                        Each email answers this question and calls out what
                        changed since the last analysis.
                    </Typography>
                </Stack>

                <Stack rowGap={0.75}>
                    <Typography sx={fieldLabelSx}>Frequency</Typography>
                    <AcxSelectSingle<FrequencyOption>
                        id="kbo-question-subscription-frequency"
                        options={frequencyOptions}
                        valueField="value"
                        labelField="label"
                        defaultValue={frequencyOptions.find(
                            (option) => option.value === frequency,
                        )}
                        onChange={(option) => {
                            if (option) setFrequency(option.value);
                        }}
                        isSearchable={false}
                        menuPlacement="bottom"
                        fullWidth
                    />
                    <Typography sx={helperSx}>
                        {frequencyNote(frequency)}
                    </Typography>
                </Stack>

                {runningOnce && (
                    <AcxDateRangeInput
                        labelText="Date Range"
                        required
                        defaultStartDate={startDate}
                        defaultEndDate={endDate}
                        onSelect={(selectedStart, selectedEnd) => {
                            setStartDate(selectedStart);
                            setEndDate(selectedEnd);
                        }}
                        maxWidth="100%"
                    />
                )}

                {validating && (
                    <Stack
                        id="kbo-question-subscription-validating"
                        direction="row"
                        alignItems="center"
                        columnGap={1.5}
                        sx={{
                            backgroundColor: "#F5F8FE",
                            border: "1px solid #D6E1F8",
                            borderRadius: "8px",
                            padding: "12px 14px",
                        }}
                    >
                        <CircularProgress size={20} />
                        <Stack rowGap={0.25}>
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    lineHeight: "20px",
                                    fontWeight: 600,
                                    color: "#111827",
                                }}
                            >
                                Validating your question…
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    lineHeight: "18px",
                                    color: "#4B5563",
                                }}
                            >
                                KBo is making sure it can answer this from your
                                conversations. This only takes a moment.
                            </Typography>
                        </Stack>
                    </Stack>
                )}

                {rejected && (
                    <Stack
                        id="kbo-question-subscription-rejected"
                        direction="row"
                        alignItems="flex-start"
                        columnGap={1.5}
                        sx={{
                            backgroundColor: "#FEF6F5",
                            border: "1px solid #F6D3CF",
                            borderRadius: "8px",
                            padding: "12px 14px",
                        }}
                    >
                        <ErrorRounded
                            sx={{
                                fontSize: 20,
                                color: "#B42318",
                                flexShrink: 0,
                                marginTop: "1px",
                            }}
                        />
                        <Stack rowGap={0.75} minWidth={0}>
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    lineHeight: "20px",
                                    fontWeight: 600,
                                    color: "#111827",
                                }}
                            >
                                KBo can't research this question
                            </Typography>
                            <Typography sx={rejectionTextSx}>
                                KBo either couldn't understand it, or your
                                account doesn't have the data needed to research
                                this. Try rewording it or asking about a
                                different topic.
                            </Typography>
                            {rejection?.reason && (
                                <Typography sx={rejectionTextSx}>
                                    {rejection.reason}
                                </Typography>
                            )}
                            <Typography sx={rejectionTextSx}>
                                Think this should work? Contact your Authenticx
                                team.
                            </Typography>
                        </Stack>
                    </Stack>
                )}

                {saveError && (
                    <AcxMessageBox
                        variant="error"
                        sx={{ margin: 0 }}
                        childrenSx={{ fontSize: 13, fontWeight: 500 }}
                    >
                        {saveError}
                    </AcxMessageBox>
                )}
            </Stack>

            <Box sx={{ padding: "12px 24px 20px" }}>
                {confirmingDelete ? (
                    <Stack rowGap={1.5}>
                        <AcxMessageBox
                            variant="warning"
                            sx={{
                                justifyContent: "flex-start",
                                alignItems: "center",
                                padding: "10px 12px",
                                gap: "8px",
                                margin: 0,
                            }}
                            childrenSx={{ fontSize: 13, lineHeight: "19px" }}
                        >
                            Delete this subscription? You'll stop receiving its
                            emails.
                        </AcxMessageBox>
                        <Stack
                            direction="row"
                            justifyContent="flex-end"
                            columnGap={1}
                        >
                            <AcxButton
                                id="kbo-question-subscription-cancel-delete"
                                color="primary"
                                variant="outlined"
                                onClick={() => setConfirmingDelete(false)}
                                disabled={deleting}
                            >
                                Cancel
                            </AcxButton>
                            <AcxButton
                                id="kbo-question-subscription-confirm-delete"
                                color="error"
                                variant="contained"
                                onClick={onDelete}
                                loading={deleting}
                            >
                                Delete
                            </AcxButton>
                        </Stack>
                    </Stack>
                ) : (
                    <Stack direction="row" alignItems="center" columnGap={1}>
                        {isEdit && (
                            <AcxButton
                                id="kbo-question-subscription-delete"
                                color="error"
                                variant="outlined"
                                onClick={() => setConfirmingDelete(true)}
                                disabled={busy}
                            >
                                Delete
                            </AcxButton>
                        )}
                        <Box flex={1} />
                        <AcxButton
                            id="kbo-question-subscription-cancel"
                            color="primary"
                            variant="outlined"
                            onClick={close}
                            disabled={busy}
                        >
                            Cancel
                        </AcxButton>
                        <AcxButton
                            id="kbo-question-subscription-save"
                            color="primary"
                            variant="contained"
                            onClick={submit}
                            loading={saving}
                            disabled={!canSave}
                        >
                            {validating
                                ? "Validating…"
                                : rejected
                                ? "Try Again"
                                : isEdit
                                ? "Save"
                                : runningOnce
                                ? "Run Now"
                                : "Add Subscription"}
                        </AcxButton>
                    </Stack>
                )}
            </Box>
        </Dialog>
    );
}
